import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';
import { editorDe, opcionesDe, type EditableLeadField } from '@/lib/lead-fields';

/**
 * EL EDITOR DE UN CAMPO DE LA FICHA, SEGÚN SU TIPO (tanda 6): desplegable si el campo trae
 * opciones, número si es numérico y texto en lo demás (`editorDe`). Es solo el control: la
 * etiqueta la pone quien lo usa (la ficha, o la cabecera de la columna en la tabla).
 *
 * El desplegable guarda al elegir; el texto y el número, al salir del campo o con Enter.
 * `onGuardar` solo se llama si el valor cambió, ya sin espacios a los lados. Si devuelve una
 * promesa y falla, el control vuelve al valor guardado: no se queda mostrando lo que no entró.
 */
export interface EditorDeCampoProps {
  campo: Pick<EditableLeadField, 'key' | 'label' | 'tipo' | 'opciones'> & { placeholder?: string };
  /** Lo guardado hoy, como texto ('' si está vacío). */
  valor: string;
  onGuardar: (valor: string) => void | Promise<unknown>;
  /** Para la celda de una tabla: sin borde hasta que se toca, y ocupa todo el ancho. */
  compacto?: boolean;
  /** No se cambia desde aquí (la etapa de un cliente ya cerrado). */
  deshabilitado?: boolean;
  /** Nace con el cursor dentro: la celda de número de la tabla, que se abre al tocarla. */
  enfocado?: boolean;
  className?: string;
}

const BASE =
  'h-8 min-w-0 rounded-md border text-base focus:border-brand-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 md:text-sm dark:text-slate-100';
// En el teléfono, 44 px de alto y letra de 16 px (con menos, iOS acerca la página al enfocar).
const NORMAL = 'min-h-[44px] flex-1 border-surface-border bg-white px-2 md:min-h-0 dark:border-slate-700 dark:bg-slate-800';
const COMPACTO =
  'w-full border-transparent bg-transparent px-1.5 hover:border-surface-border focus:bg-white dark:hover:border-slate-700 dark:focus:bg-slate-800';

export function EditorDeCampo({ campo, valor, onGuardar, compacto = false, deshabilitado = false, enfocado = false, className }: EditorDeCampoProps) {
  const [borrador, setBorrador] = useState(valor);
  useEffect(() => setBorrador(valor), [valor]);

  function guardar(nuevo: string) {
    const limpio = nuevo.trim();
    if (limpio === valor) return;
    void Promise.resolve(onGuardar(limpio)).catch(() => setBorrador(valor));
  }

  const clase = cn(BASE, compacto ? COMPACTO : NORMAL, className);
  const editor = editorDe(campo, valor);

  if (editor === 'opciones') {
    return (
      <select
        aria-label={campo.label}
        value={borrador}
        disabled={deshabilitado}
        autoFocus={enfocado}
        onChange={(e) => {
          setBorrador(e.target.value);
          guardar(e.target.value);
        }}
        className={clase}
      >
        {opcionesDe(campo, valor).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      aria-label={campo.label}
      type={editor === 'numero' ? 'number' : 'text'}
      inputMode={editor === 'numero' ? 'decimal' : undefined}
      min={editor === 'numero' ? 0 : undefined}
      value={borrador}
      disabled={deshabilitado}
      autoFocus={enfocado}
      placeholder={campo.placeholder}
      onChange={(e) => setBorrador(e.target.value)}
      onBlur={() => guardar(borrador)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
      className={clase}
    />
  );
}
